import { FotaAPIService } from './AxiosAPIService';
import { logger } from './SharedService';
import type {
  AudioAnnouncementUploadRequest,
  AudioAnnouncementUploadResponse,
  AudioAnnouncementFile,
  UpdateAudioFileNameRequest,
  DeleteAudioAnnouncementFileRequest,
  AudioAnnouncementDevicesResponse,
  SendAudioToDevicesRequest,
  SendAudioToDevicesResponse,
  AbortAudioRequest,
  AbortAudioResponse,
  AbortBroadcastRequest,
  SendHistoryResponse,
  SendHistoryItem,
  SendHistoryApiResponse,
  SearchBroadcastResponse,
} from '../types/AudioAnnouncement';

export const getListAudioAnnouncementFiles = async (
  fleetId: string,
): Promise<AudioAnnouncementFile[]> => {
  const path = `/audio-announcement/fleets/${fleetId}/audio-files`;
  logger.info('GET', 'REQUEST', 'getListAudioAnnouncementFiles', path);
  const { data } = await FotaAPIService.api
    .get<AudioAnnouncementFile[]>(path)
    .catch((e) => Promise.reject(e.response?.data ?? e));
  logger.info('GET', 'RESPONSE', 'getListAudioAnnouncementFiles', data);
  return data;
};

export const uploadAudioAnnouncementFiles = async ({
  fleetId,
  files,
}: AudioAnnouncementUploadRequest): Promise<AudioAnnouncementUploadResponse> => {
  const path = `/audio-announcement/fleets/${fleetId}/audio-files`;
  logger.info('POST', 'REQUEST', 'uploadAudioAnnouncementFiles', path);

  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const { data } = await FotaAPIService.api
    .post<AudioAnnouncementUploadResponse>(path, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
    .catch((e) => Promise.reject(e.response?.data ?? e));

  logger.info('POST', 'RESPONSE', 'uploadAudioAnnouncementFiles', data);
  return data;
};

export const patchUpdateAudioFileName = async ({
  fleetId,
  audioId,
  fileName,
}: UpdateAudioFileNameRequest): Promise<AudioAnnouncementFile> => {
  const path = `/audio-announcement/fleets/${fleetId}/audio-files/${audioId}`;
  logger.info('PATCH', 'REQUEST', 'patchUpdateAudioFileName', path);

  const { data } = await FotaAPIService.api
    .patch<AudioAnnouncementFile>(path, { fileName })
    .catch((e) => Promise.reject(e.response?.data ?? e));

  logger.info('PATCH', 'RESPONSE', 'patchUpdateAudioFileName', data);
  return data;
};

export const deleteAudioAnnouncementFile = async ({
  fleetId,
  audioId,
}: DeleteAudioAnnouncementFileRequest): Promise<void> => {
  const path = `/audio-announcement/fleets/${fleetId}/audio-files/${audioId}`;
  logger.info('DELETE', 'REQUEST', 'deleteAudioAnnouncementFile', path);

  await FotaAPIService.api
    .delete(path)
    .catch((e) => Promise.reject(e.response?.data ?? e));

  logger.info('DELETE', 'RESPONSE', 'deleteAudioAnnouncementFile');
};

export const getAudioAnnouncementDevices = async (
  fleetId: string,
  audioId: string,
): Promise<AudioAnnouncementDevicesResponse> => {
  const path = `/audio-announcement/fleets/${fleetId}/audio-files/${audioId}/devices`;
  logger.info('GET', 'REQUEST', 'getAudioAnnouncementDevices', path);
  const { data } = await FotaAPIService.api
    .get<AudioAnnouncementDevicesResponse>(path)
    .catch((e) => Promise.reject(e.response?.data ?? e));
  logger.info('GET', 'RESPONSE', 'getAudioAnnouncementDevices', data);
  return data;
};

export const postSendAudioToDevices = async ({
  fleetId,
  audioId,
  devices,
  timestampSecEpochToTimeout,
}: SendAudioToDevicesRequest): Promise<SendAudioToDevicesResponse> => {
  const path = `/audio-announcement/fleets/${fleetId}/audio-files/${audioId}/send`;
  logger.info('POST', 'REQUEST', 'postSendAudioToDevices', path);
  const { data } = await FotaAPIService.api
    .post<SendAudioToDevicesResponse>(path, { timestampSecEpochToTimeout, devices })
    .catch((e) => Promise.reject(e.response?.data ?? e));
  logger.info('POST', 'RESPONSE', 'postSendAudioToDevices', data);
  return data;
};

export const postAbortAudio = async ({
  fleetId,
  audioId,
  devices,
}: AbortAudioRequest): Promise<AbortAudioResponse> => {
  const path = `/audio-announcement/fleets/${fleetId}/audio-files/${audioId}/abort`;
  logger.info('POST', 'REQUEST', 'postAbortAudio', path);
  const { data } = await FotaAPIService.api
    .post<AbortAudioResponse>(path, { devices })
    .catch((e) => Promise.reject(e.response?.data ?? e));
  logger.info('POST', 'RESPONSE', 'postAbortAudio', data);
  return data;
};

export const postAbortBroadcast = async ({
  fleetId,
  audioId,
  sendId,
  devices,
}: AbortBroadcastRequest): Promise<AbortAudioResponse> => {
  const path = `/audio-announcement/fleets/${fleetId}/audio-files/${audioId}/${sendId}/broadcast/abort`;
  logger.info('POST', 'REQUEST', 'postAbortBroadcast', path);
  const { data } = await FotaAPIService.api
    .post<AbortAudioResponse>(path, { devices })
    .catch((e) => Promise.reject(e.response?.data ?? e));
  logger.info('POST', 'RESPONSE', 'postAbortBroadcast', data);
  return data;
};

export const getAudioAnnouncementSendHistory = async (
  fleetId: string,
  params: { limit: number; skip: number; sortOrder: string },
): Promise<SendHistoryResponse> => {
  const { limit, skip, sortOrder } = params;
  const query = `?limit=${limit}&skip=${skip}&sortOrder=${sortOrder}`;
  const path = `/audio-announcement/fleets/${fleetId}/audio-announcement-send-history${query}`;
  logger.info('GET', 'REQUEST', 'getAudioAnnouncementSendHistory', path);
  const { data } = await FotaAPIService.api
    .get<SendHistoryResponse | SendHistoryApiResponse | SendHistoryItem[]>(path)
    .catch((e) => Promise.reject(e.response?.data ?? e));
  logger.info('GET', 'RESPONSE', 'getAudioAnnouncementSendHistory', data);
  if (data && typeof data === 'object' && 'totalNumberOfRecords' in data && Array.isArray((data as SendHistoryApiResponse).data)) {
    const api = data as SendHistoryApiResponse;
    return { results: api.data, total: api.totalNumberOfRecords };
  }
  if (Array.isArray(data)) {
    return { results: data };
  }
  if (data && typeof data === 'object' && 'results' in data) {
    return data as SendHistoryResponse;
  }
  return { results: [] };
};

export const getSearchBroadcast = async (
  fleetId: string,
  audioAnnouncementId: string,
  sendId: string,
  search: string,
): Promise<SearchBroadcastResponse> => {
  const encoded = encodeURIComponent(search.trim());
  const path = `/audio-announcement/${fleetId}/${audioAnnouncementId}/${sendId}/search?search=${encoded}`;
  logger.info('GET', 'REQUEST', 'getSearchBroadcast', path);
  const { data } = await FotaAPIService.api
    .get<SearchBroadcastResponse>(path)
    .catch((e) => Promise.reject(e.response?.data ?? e));
  logger.info('GET', 'RESPONSE', 'getSearchBroadcast', data);
  return data ?? { totalRecords: 0, data: [] };
};

export const getDeviceAudioQueuedStatus = async (
  fleetId: string,
  deviceId: string,
  audioId: string,
): Promise<any> => {
  const path = `/audio-announcement/fleets/${fleetId}/devices/${deviceId}/audio-queued/${audioId}`;

  logger.info('GET', 'REQUEST', 'getDeviceAudioQueuedStatus', path);

  const { data } = await FotaAPIService.api
    .get(path)
    .catch((e) => Promise.reject(e.response?.data ?? e));

  logger.info('GET', 'RESPONSE', 'getDeviceAudioQueuedStatus', data);

  return data;
};
